import type { CameraParams } from '@/lib/universe/views'

// One camera model shared by the WebGL renderer and anything drawn on top of it in the page
// (anything that must line up with the hole), so both always agree on where a point in space lands.

export interface CameraBasis {
    px: number
    py: number
    pz: number
    rx: number
    ry: number
    rz: number
    ux: number
    uy: number
    uz: number
    fx: number
    fy: number
    fz: number
    tanHalf: number
    aspect: number
    distance: number
    shiftX: number
    shiftY: number
}

const DEG = Math.PI / 180

export const createBasis = (): CameraBasis => ({
    px: 0,
    py: 0,
    pz: 1,
    rx: 1,
    ry: 0,
    rz: 0,
    ux: 0,
    uy: 1,
    uz: 0,
    fx: 0,
    fy: 0,
    fz: -1,
    tanHalf: 0.36,
    aspect: 1.6,
    distance: 1,
    shiftX: 0,
    shiftY: 0
})

export const computeCamera = (
    params: CameraParams,
    boost: number,
    tilt: number,
    width: number,
    height: number,
    out: CameraBasis = createBasis()
): CameraBasis => {
    const distance = params.distance * (1 - 0.07 * boost)
    const inclination = (params.inclination + 1.2 * tilt) * DEG
    const azimuth = params.azimuth * DEG
    const roll = (params.roll + 3.2 * tilt) * DEG
    const cosI = Math.cos(inclination)

    const px = distance * cosI * Math.sin(azimuth)
    const py = distance * Math.sin(inclination)
    const pz = distance * cosI * Math.cos(azimuth)

    const fLength = Math.hypot(px, py, pz) || 1
    const fx = -px / fLength
    const fy = -py / fLength
    const fz = -pz / fLength

    const rLength = Math.hypot(fz, fx) || 1
    const rx = -fz / rLength
    const rz = fx / rLength

    const ux = -rz * fy
    const uy = rz * fx - rx * fz
    const uz = rx * fy

    const cr = Math.cos(roll)
    const sr = Math.sin(roll)
    const aspect = width / Math.max(1, height)

    let tanHalf = Math.tan(params.fov * DEG * 0.5)

    if (aspect < 1) tanHalf *= Math.pow(1 / aspect, 0.7) * 1.06

    out.px = px
    out.py = py
    out.pz = pz
    out.rx = rx * cr + ux * sr
    out.ry = uy * sr
    out.rz = rz * cr + uz * sr
    out.ux = ux * cr - rx * sr
    out.uy = uy * cr
    out.uz = uz * cr - rz * sr
    out.fx = fx
    out.fy = fy
    out.fz = fz
    out.tanHalf = tanHalf
    out.aspect = aspect
    out.distance = distance
    out.shiftX = params.shiftX
    out.shiftY = params.shiftY

    return out
}

export interface ScreenPoint {
    x: number
    y: number
    depth: number
}

// Projects a point in space (units of the horizon radius) to screen pixels. Returns false behind the camera.
export const projectPoint = (
    basis: CameraBasis,
    x: number,
    y: number,
    z: number,
    width: number,
    height: number,
    out: ScreenPoint
): boolean => {
    const dx = x - basis.px
    const dy = y - basis.py
    const dz = z - basis.pz
    const depth = dx * basis.fx + dy * basis.fy + dz * basis.fz

    if (depth < 0.5) return false

    const cx = dx * basis.rx + dy * basis.ry + dz * basis.rz
    const cy = dx * basis.ux + dy * basis.uy + dz * basis.uz
    const sx = cx / (depth * basis.tanHalf) + basis.shiftX * basis.aspect
    const sy = cy / (depth * basis.tanHalf) + basis.shiftY

    out.x = (sx / basis.aspect + 1) * 0.5 * width
    out.y = (1 - sy) * 0.5 * height
    out.depth = depth

    return true
}

// Apparent radius of the shadow in pixels: sin(angle) = b * sqrt(1 - 1 / r) / r for a static observer
export const shadowRadius = (basis: CameraBasis, height: number): number => {
    const r = Math.max(basis.distance, 1.6)
    const angle = Math.asin(Math.min(0.99, (2.598 * Math.sqrt(1 - 1 / r)) / r))

    return (Math.tan(angle) / basis.tanHalf) * height * 0.5
}
